<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end form-offcanvas"
    tabindex="-1"
    id="offcanvaInfo"
    ref="offcanvaInfo"
    data-bs-backdrop="static"
  >
    <!-- data-bs-backdrop="static" -->
    <div class="offcanvas-header border-bottom bg-white py-3">
      <h6 class="offcanvas-title fw-bold mb-0 d-flex align-items-center gap-2">
        <i class="fa-solid fa-box-open text-theme" />
        {{ $t('Product Detail')  }}
      </h6>
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div
      class="offcanvas-body"
      v-if="loading"
    >
      <placeholder-content :is-load="true" />
    </div>
    <div
      class="offcanvas-body"
      v-else-if="!hasPermission"
    >
      <access-denied/>
    </div>
    <div
      class="offcanvas-body bg-body-secondary p-3"
      v-else
    >
      <!-- ───── Hero: รูป + ชื่อ + badges ───── -->
      <div class="form-card">
        <div class="d-flex gap-3 px-3 py-3">
          <img
            v-if="data.thumbnail_url"
            @click="showImage(data.image_url)"
            :src="$storeIndex.getImageUrl(data.thumbnail_url)"
            @error="$storeIndex.onImageError"
            class="rounded border flex-shrink-0"
            style="width: 72px; height: 72px; object-fit: cover; cursor: pointer;"
          >
          <div class="flex-grow-1" style="min-width: 0;">
            <div class="fw-bold" style="font-size: 18px;">
              {{ data.name }}
            </div>
            <div class="d-flex flex-wrap gap-1 mt-2">
              <span :class="['badge', data.active? 'bg-success': 'bg-danger']">
                {{ data.active? $t('Active'): $t('Inactive') }}
              </span>
              <span class="badge text-bg-light border">
                {{ $t(data.type) }}
              </span>
              <span
                v-if="data.recommend"
                class="badge bg-warning text-dark"
              >
                ⭐ {{ $t('Recommend') }}
              </span>
              <span
                v-if="data.show_in_menu === false"
                class="badge bg-theme text-white"
              >
                🎁 {{ $t('Reward-only') }}
              </span>
              <span
                v-if="data.dine_in_only"
                class="badge text-bg-light border"
              >
                🍽️ {{ $t('Dine-in only') }}
              </span>
              <span
                v-if="data.pos_only"
                class="badge text-bg-light border"
              >
                🧑‍🍳 {{ $t('POS staff only') }}
              </span>
            </div>
            <div class="text-secondary small mt-2">
              {{ $t('Last Update') + ': ' + formatDateTime(data.updated_at) }}
            </div>
          </div>
        </div>
      </div>

      <!-- ───── ข้อมูลสินค้า ───── -->
      <div class="form-card">
        <div class="form-section-header">
          <i class="fa-solid fa-circle-info" />
          <span>{{ $t('Product Information') }}</span>
        </div>
        <div class="px-3 pt-3">
          <div class="row g-3" style="font-size: 13px;">
            <div class="col-6 col-md-4">
              <div class="text-secondary">{{ $t('SKU') }}</div>
              <div class="fw-medium">{{ data.code || '-' }}</div>
            </div>
            <div class="col-6 col-md-4">
              <div class="text-secondary">{{ $t('Category') }}</div>
              <div class="fw-medium">{{ data.category? $ln(data, 'category'): '-' }}</div>
            </div>
            <div class="col-6 col-md-4">
              <div class="text-secondary">{{ $t('VAT') }}</div>
              <div class="fw-medium">{{ $t(data.tax_status) }}</div>
            </div>
            <div class="col-6 col-md-4">
              <div class="text-secondary">{{ $t('Price') }}</div>
              <div class="fw-medium">{{ formatDigit(data.price) }} ฿</div>
            </div>
            <div
              class="col-6 col-md-4"
              v-if="data.compare_at_price"
            >
              <div class="text-secondary">{{ $t('Compare at price') }}</div>
              <div class="fw-medium text-secondary text-decoration-line-through">
                {{ formatDigit(data.compare_at_price) }} ฿
              </div>
            </div>
            <div class="col-6 col-md-4">
              <div class="text-secondary">{{ $t('Unit Cost') }}</div>
              <div class="fw-medium">{{ formatDigit(data.cost) }} ฿</div>
            </div>
            <div class="col-6 col-md-4">
              <div class="text-secondary">{{ $t('Serving Size') }}</div>
              <div class="fw-medium">
                {{ data.size_value && data.size_unit? data.size_value + ' ' + $t(data.size_unit): '-' }}
              </div>
            </div>
            <div class="col-6 col-md-4">
              <div class="text-secondary">{{ $t('Sales Unit') }}</div>
              <div class="fw-medium">{{ data.unit? $t(data.unit): '-' }}</div>
            </div>
            <div class="col-6 col-md-4">
              <div class="text-secondary">{{ $t('Create By') }}</div>
              <div class="fw-medium">{{ data.created_by }}</div>
            </div>
            <div class="col-12">
              <div class="text-secondary">{{ $t('Barcode') }}</div>
              <vue3-barcode
                v-if="data.barcode"
                style="overflow-x: hidden !important; margin-right: 0px;"
                :value="data.barcode"
                format="CODE128"
                :display-value="true"
                :text-align="'left'"
                :font-size="12"
                :width="2"
                :height="28"
                margin="0"
              />
              <div
                v-else
                class="fw-medium"
              >
                -
              </div>
            </div>
          </div>

          <template
            v-if="data.description"
          >
            <div
              class="text-secondary mt-3 mb-1"
              style="font-size: 13px;"
            >
              {{ $t('Description') }}
            </div>
            <div
              class="ql-editor p-0"
              v-html="data.description"
            />
          </template>
        </div>
      </div>

      <!-- ───── ตัวเลือก (Attributes) ───── -->
      <div
        class="form-card"
        v-if="data.attributes && data.attributes.length > 0"
      >
        <div class="form-section-header">
          <i class="fa-solid fa-sliders" />
          <span>{{ $t('Attributes & Options') }}</span>
        </div>
        <div class="px-3 pt-3">
          <div class="row g-3" style="font-size: 13px;">
            <div
              class="col-12"
              v-for="(attr, i) in data.attributes"
              :key="i"
            >
              <div class="text-secondary">{{ $ln(attr) }}</div>
              <div class="d-flex flex-wrap gap-1 mt-1">
                <span
                  class="badge text-bg-light border"
                  v-for="(opt, j) in attr.options"
                  :key="j"
                >
                  {{ $t(opt) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ───── Variants ───── -->
      <div
        class="form-card"
        v-if="datatableVariants.items.length > 0"
      >
        <div class="form-section-header">
          <i class="fa-solid fa-boxes-stacked" />
          <span>{{ $t('Variants') }}</span>
        </div>
        <div class="overflow-x-auto">
          <vue3-easy-data-table
            ref="datatableVariants"
            :headers="datatableVariants.headers($t)"
            :items="datatableVariants.items"
            :loading="datatableVariants.loading"
            :theme-color="'#515151'"
            :hide-footer="true"
            :hide-rows-per-page="true"
            :empty-message="$t('No Available Data')"
            :table-min-height="null"
            :body-item-class-name="bodyItemClassNameFunction"
            table-class-name='customize-table-hide-footer-in-offcanvas'
          >
            <template #header-price="header">
              <span class="w-100 text-end">
                {{ header.text }}
              </span>
            </template>
            <template #header-cost="header">
              <span class="w-100 text-end">
                {{ header.text }}
              </span>
            </template>
            <template #header-size_value="header">
              <span class="w-100 text-end">
                {{ header.text }}
              </span>
            </template>
            <template #header-unit="header">
              <span class="w-100 text-end">
                {{ header.text }}
              </span>
            </template>
            <template #item-image="item">
              <img
                @click="showImage(item.image_url)"
                :src="$storeIndex.getImageUrl(item.thumbnail_url)"
                @error="$storeIndex.onImageError"
                class="img-table"
                :style="item.image_url? 'cursor: pointer;': ''"
              >
            </template>
            <template #item-name="item">
              <div>
                {{ $ln(item) }}
                <i
                  v-if="item.dine_in_only"
                  class="fa-solid fa-utensils text-secondary ms-1"
                  style="font-size: 11px;"
                  :title="$t('Dine-in only')"
                />
              </div>
              <span>
                <b>
                  {{ $t('SKU') + ': ' }}
                </b>
              </span>
              <span
                class="text-secondary"
              >
                {{ item.code }}
              </span>
            </template>
            <template #item-price="item">
              <div>{{ formatDigit(item.price) }}</div>
              <div
                v-if="item.compare_at_price"
                class="text-secondary text-decoration-line-through"
                style="font-size: 11px;"
              >
                {{ formatDigit(item.compare_at_price) }}
              </div>
            </template>
            <template #item-cost="item">
              {{ formatDigit(item.cost) }}
            </template>
            <template #item-size_value="item">
              {{ item.size_value && item.size_unit? formatDigit(item.size_value) + ' ' + $t(item.size_unit): '-' }}
            </template>
            <template #item-unit="item">
              {{ $t(item.unit) }}
            </template>
            <template #loading>
              <i class="fa-solid fa-spinner fa-spin fa-2xl" />
            </template>
          </vue3-easy-data-table>
        </div>
      </div>
    </div>
    <div
      class="bg-light border-top justify-content-center modal-footer py-2"
      v-if="!loading && hasPermission"
    >
      <div class="btn-group dropdown">
        <button
          type="button"
          class="btn btn-sm btn-theme dropdown-toggle"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          @mouseenter="isHovered = true"
          @mouseleave="isHovered = false"
        >
          <i class="fa-solid fa-ellipsis me-1" :class="isHovered? 'fa-fade': ''" />
          {{ $t('More Action') }}
        </button>
        <ul class="dropdown-menu">
          <li>
            <a
              class="dropdown-item"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
              @click="() => {
                $parent.offcanvasFroms.open = true;
                $parent.offcanvasFroms.id = id;
                $parent.offcanvasFroms.type = 'EDIT';
              }"
            >
              <i class="fa-solid fa-square-pen" />
              {{ $t('Edit') }}
            </a>
          </li>
          <li v-if="$hasFeature('feature_inventory')">
            <a
              class="dropdown-item"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
              @click="() => {
                $parent.offcanvasRecipeFrom.open = true;
                $parent.offcanvasRecipeFrom.id = id;
              }"
            >
              <i class="fa-solid fa-book-open" />
              {{ $t('Recipe') }}
            </a>
          </li>
          <li>
            <a
              class="dropdown-item"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
              @click="() => {
                $parent.offcanvasModifierForm.open = true;
                $parent.offcanvasModifierForm.id = id;
              }"
            >
              <i class="fa-solid fa-sliders" />
              {{ $t('Modifier') }}
            </a>
          </li>
          <li>
            <a
              class="dropdown-item"
              @click="handleDelete()"
            >
              <i class="fa-solid fa-trash-can" />
              {{ $t('Delete') }}
            </a>
          </li>
        </ul>
      </div>
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2 ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
  <div
    class="modal fade"
    id="modalImage"
    ref="modalImage"
    aria-labelledby="modalImageLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <img
        :src="$storeIndex.getImageUrl(modalImage.image)"
        @error="$storeIndex.onImageError"
        class="d-block w-100"
        data-bs-dismiss="modal"
      >
    </div>
  </div>
</template>

<script lang="js">
import AccessDenied from "@/plugins/AccessDenied.vue";
import Vue3EasyDataTable from 'vue3-easy-data-table';
import { Products } from '@/services/endpoint/products.service';
import { Inventories, InventoryLots } from '@/services/endpoint/inventories.service';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import Ladda from '@/plugins/Ladda.vue';
import moment from "moment";
import numeral from 'numeral';
import FieldsVariants from "./assets/template/datatable/FieldsVariants";
import Popper from "vue3-popper";
import Vue3Barcode from 'vue3-barcode'

export default {
  props: {
    id: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      hasPermission: false,
      modalImage: {
        open: false,
        image: ''
      },
      datatableVariants: {
        loading: false,
        headers: FieldsVariants,
        items: [],
        total: 0,
        serverOptions: {
          page: 1,
          rowsPerPage: 10
        },
        params: {
          limit: 10,
          page: 1,
          search: ''
        }
      },
      isHovered: false,
      loading: true,
      data: {
        name: '',
        code: '',
        quantity: 0,
        cost: 0,
        unit_id: '1',
      },
    }
  },
  components: {
    AccessDenied,
    Vue3EasyDataTable,
    PlaceholderContent,
    Ladda,
    Popper,
    Vue3Barcode
  },
  methods: {
    async showImage(image) {
      if (!image) {
        return;
      }
      this.modalImage.image = image;
      $(this.$refs.modalImage).modal('show');
    },
    bodyItemClassNameFunction(column, rowNumber) {
      let className = 'align-top py-1 px-2 border-0';
      const columnCenter = ['received_date', 'expiry_date', 'created_at'];
      const columnEnd = ['price', 'cost', 'size_value', 'unit'];
      if (columnCenter.includes(column)) {
        className += ' text-center';
      }
      if (columnEnd.includes(column)) {
        className += ' text-end';
      }
      return className;
    },
    formatDigit(value) {
      if ((value < 0.01) && value != 0) {
        return numeral(value).format("0,0.0000");
      }
      return numeral(value).format("0,0.00");
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.0000");
    },
    formatNumberTwoDigit(value) {
      return numeral(value).format("0,0.00");
    },
    async getInfoData() {
      this.loading = true;
      await Products.getInfo(this.id).then(res => {
        this.data = res.data.data;
        this.datatableVariants.items = res.data.data.variants;
        this.hasPermission = true;
        this.loading = false;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        }
        this.loading = false;
			  throw err;
      });
    },
    formatDate(value) {
      return moment(value).format('DD/MM/YYYY');
    },
    formatDateTime(value) {
      return moment(value).format('YYYY-MM-DD HH:mm:ss');
    },
    async handleDelete() {
      try {
        let self = this;
        let title = this.$t("Delete Product") + '?';
        let html = this.$t("You will not be able to undo this action.");
        let confirmButtonText =
          '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm");
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: title,
          // text: "You will not be able to undo this action.",
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          preConfirm: async function () {
            return Products.delete(self.id)
              .then((response) => {
                self.$parent.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Delete Product Success!'));
                $(self.$refs.offcanvaInfo).offcanvas('hide');
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
    async handleEditLot() {
      try {
        let self = this;
        let title = this.$t("Delete Lot") + '?';
        let html = this.$t("Deleting this lot may affect past cost calculations");
        let confirmButtonText =
          '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm");
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: title,
          // text: "You will not be able to undo this action.",
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          preConfirm: async function () {
            return Inventories.delete(self.inventoryId)
              .then((response) => {
                self.$parent.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Delete Product Success!'));
                $(self.$refs.offcanvaInfo).offcanvas('hide');
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
    async handleDeleteLot() {
      try {
        let self = this;
        let title = this.$t("Delete Lot") + '?';
        let html = this.$t("Deleting this lot may affect past cost calculations");
        let confirmButtonText =
          '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm");
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: title,
          // text: "You will not be able to undo this action.",
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          preConfirm: async function () {
            return InventoryLots.delete(self.inventoryId)
              .then((response) => {
                self.$parent.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Delete Product Success!'));
                $(self.$refs.offcanvaInfo).offcanvas('hide');
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
  },
  async mounted() {
    $(this.$refs.offcanvaInfo).offcanvas('show');

    $(this.$refs.offcanvaInfo).on('shown.bs.offcanvas',async () => {
      try {
        await this.getInfoData();
      } catch (err) {
      }
    });

    $(this.$refs.offcanvaInfo).on('hidden.bs.offcanvas', () => {
      this.$emit('close-info');
      // this.$parent.formInfo.open = false;
    });
  },
  computed: {

  },
  watch: {
  }
}
</script>
